import type { ReactNode } from 'react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Dialog } from 'ui/components/Dialog/Dialog'
import { TimeSeriesTableHelpDemo } from './TimeSeriesTableHelpDemo'
import styles from './TimeSeriesTableHelpDialog.module.less'
import {
  getTimeSeriesTableHelpTabs,
  TimeSeriesTableHelpTab,
  TimeSeriesTableHelpTabId,
} from './timeSeriesTableHelp.helper'

type TimeSeriesTableHelpDialogProps = {
  open: boolean
  onClose: () => void
  availableTabIds: TimeSeriesTableHelpTabId[]
  hasSelectionCheckboxes: boolean
}

const TITLE_ID = 'time-series-table-help-title'

const TAB_BASE_CLASSES =
  'rounded-[5px] px-[14px] py-[9px] text-sm text-grey-6 transition-colors duration-150 ease-in-out hover:bg-grey-2 hover:text-primary-7 hover:cursor-pointer'

function tabElementId(id: TimeSeriesTableHelpTabId): string {
  return `time-series-help-tab-${id}`
}

function tabPanelElementId(id: TimeSeriesTableHelpTabId): string {
  return `time-series-help-tabpanel-${id}`
}

export function TimeSeriesTableHelpDialog({
  open,
  onClose,
  availableTabIds,
  hasSelectionCheckboxes,
}: TimeSeriesTableHelpDialogProps): ReactNode {
  const { t } = useTranslation()
  const [selectedTabId, setSelectedTabId] = useState<TimeSeriesTableHelpTabId>(availableTabIds[0])

  const tabs = getTimeSeriesTableHelpTabs(t, availableTabIds, hasSelectionCheckboxes)
  const activeTab = tabs.find((tab) => tab.id === selectedTabId) ?? tabs[0]

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      aria-labelledby={TITLE_ID}
      slotProps={{ paper: { className: 'w-[900px] max-w-full rounded-[10px]' } }}
    >
      <div className="px-[30px] pb-8 pt-7">
        <div className="flex items-start justify-between gap-4">
          <h2
            id={TITLE_ID}
            className={`${styles.TimeSeriesTableHelpDialog_Title} m-0 text-xl font-bold tracking-[0.01em] text-primary-7`}
          >
            {t('Time series table features')}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('Close')}
            className="rounded-[5px] px-[6px] py-1 text-xl leading-none text-grey-5 transition-colors duration-150 hover:bg-grey-2 hover:text-grey-7 hover:cursor-pointer"
          >
            <i className="fal fa-times" aria-hidden="true" />
          </button>
        </div>

        <TabList tabs={tabs} activeId={activeTab.id} onSelect={setSelectedTabId} />

        <div
          role="tabpanel"
          id={tabPanelElementId(activeTab.id)}
          aria-labelledby={tabElementId(activeTab.id)}
          className="mt-[22px] flex items-start gap-8"
        >
          <TabContent tab={activeTab} />
          <TimeSeriesTableHelpDemo
            tabId={activeTab.id}
            hint={activeTab.demoHint}
            hasSelectionCheckboxes={hasSelectionCheckboxes}
          />
        </div>
      </div>
    </Dialog>
  )
}

type TabListProps = {
  tabs: TimeSeriesTableHelpTab[]
  activeId: TimeSeriesTableHelpTabId
  onSelect: (id: TimeSeriesTableHelpTabId) => void
}

function TabList({ tabs, activeId, onSelect }: TabListProps): ReactNode {
  return (
    <div role="tablist" className="mt-5 flex flex-wrap gap-1 border-b border-grey-3 pb-[14px]">
      {tabs.map((tab) => {
        const isActive = tab.id === activeId
        return (
          <button
            key={tab.id}
            id={tabElementId(tab.id)}
            type="button"
            role="tab"
            aria-selected={isActive}
            aria-controls={tabPanelElementId(tab.id)}
            onClick={() => onSelect(tab.id)}
            className={`${TAB_BASE_CLASSES} ${isActive ? 'bg-grey-2 font-semibold text-primary-7' : ''}`}
          >
            {tab.label}
          </button>
        )
      })}
    </div>
  )
}

function TabContent({ tab }: { tab: TimeSeriesTableHelpTab }): ReactNode {
  const { t } = useTranslation()

  return (
    <div className="min-w-0 flex-1">
      <p className="pb-[18px] text-[0.9375rem] leading-[1.6] text-grey-7">{tab.intro}</p>

      <SectionLabel>{t('How to')}</SectionLabel>
      <ol className="m-0 mb-5 flex list-none flex-col gap-[10px] p-0">
        {tab.steps.map((step, index) => (
          <li key={step} className="flex gap-[10px]">
            <span className="flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full bg-primary-1 text-xs font-semibold text-primary-5">
              {index + 1}
            </span>
            <span className="text-sm leading-[1.55] text-grey-7">{step}</span>
          </li>
        ))}
      </ol>

      <SectionLabel>{t('Good to know')}</SectionLabel>
      <ul className="m-0 flex list-none flex-col gap-[9px] p-0">
        {tab.tips.map((tip) => (
          <li key={tip} className="flex gap-[10px]">
            <span className="ml-1 mt-[7px] h-[5px] w-[5px] shrink-0 rounded-full bg-grey-4" />
            <span className="text-[0.8125rem] leading-[1.55] text-grey-6">{tip}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}

function SectionLabel({ children }: { children: string }): ReactNode {
  return <h3 className="mb-[10px] text-xs font-semibold uppercase tracking-[0.08em] text-grey-5">{children}</h3>
}
